import React, { useState } from 'react';
import axios from 'axios'; // Assuming axios is installed
import './AdminPage.css';

interface Ticket {
  id: number;
  name: string;
  rsvpd: boolean;
  guests: Guest[];
  dietary_restrictions: string | null;
  comments: string | null;
}

interface Guest {
  id: number;
  first_name: string;
  last_name: string;
  attending_welcome_party: boolean | null;
  attending_wedding: boolean | null;
  attending_pool_party: boolean | null;
  staying_at_venue: boolean | null;
}

const AdminPage: React.FC = () => {
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false); // Changed from loggedIn
  const [error, setError] = useState('');
  const [tickets, setTickets] = useState<Ticket[]>([]); // New state for tickets

  // State for new ticket creation form
  const [newTicketName, setNewTicketName] = useState('');
  const [newGuests, setNewGuests] = useState<{ first_name: string; last_name: string }[]>([
    { first_name: '', last_name: '' } // Start with one guest field
  ]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    // In a real application, you'd send this to a secure login endpoint
    // For now, we'll just check against a hardcoded password (for demonstration)
    console.log('Expected password:', process.env.REACT_APP_ADMIN_PASSWORD);
    console.log('Entered password:', password);
    if (password === process.env.REACT_APP_ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      fetchTickets();
    } else {
      alert('Incorrect password');
    }
  };

  const fetchTickets = async () => {
    try {
      const response = await axios.get('/api/tickets', {
        headers: {
          'x-admin-password': password,
        },
      });
      setTickets(response.data);
    } catch (err: any) {
      console.error('Error fetching tickets:', err);
      if (err.response && err.response.data) {
        setError(`Failed to fetch tickets: ${err.response.data.error || err.response.statusText}`);
      } else {
        setError('Failed to fetch tickets. Check server logs.');
      }
    }
  };

  const handleCreateTicketAndGuests = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTicketName.trim() || newGuests.some(g => !g.first_name.trim() || !g.last_name.trim())) {
      setError('Ticket name and all guest names are required.');
      return;
    }

    try {
      await axios.post('/api/admin/tickets', { // New API endpoint
        ticketName: newTicketName,
        guests: newGuests,
      }, {
        headers: {
          'Content-Type': 'application/json',
          'x-admin-password': password,
        },
      });

      alert('Ticket and guests created successfully!');
      setNewTicketName('');
      setNewGuests([{ first_name: '', last_name: '' }]); // Reset form
      fetchTickets(); // Refresh the list
      setError(''); // Clear any previous errors
    } catch (err: any) {
      console.error('Error creating ticket and guests:', err);
      if (err.response && err.response.data) {
        setError(`Failed to create ticket: ${err.response.data.error || err.response.statusText}`);
      } else {
        setError('An error occurred while creating ticket and guests.');
      }
    }
  };

  const handleAddNewGuest = () => {
    setNewGuests([...newGuests, { first_name: '', last_name: '' }]);
  };

  const handleRemoveNewGuest = (indexToRemove: number) => {
    setNewGuests(newGuests.filter((_, index) => index !== indexToRemove));
  };

  const handleNewGuestChange = (index: number, field: 'first_name' | 'last_name', value: string) => {
    const updatedGuests = newGuests.map((guest, i) =>
      i === index ? { ...guest, [field]: value } : guest
    );
    setNewGuests(updatedGuests);
  };

  // Removed handleResetRsvp function

  if (!isAuthenticated) { // Changed from loggedIn
    return (
      <section className="rsvp-section py-5">
        <div className="container">
          <h2 className="text-center">Admin Login</h2>
          <div className="row">
            <div className="col-md-6 mx-auto">
              <form onSubmit={handleLogin}>
                <div className="mb-3">
                  <label htmlFor="adminPassword" className="form-label">Password</label>
                  <input
                    type="password"
                    className="form-control"
                    id="adminPassword"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
                {error && <div className="alert alert-danger">{error}</div>}
                <button type="submit" className="btn-like-tab">Login</button>
              </form>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="rsvp-section py-5">
      <div className="container">
        <h2 className="admin-panel-title">Admin Panel</h2>

        <div className="mb-4">
          {/* Create New Ticket Form */}
          <div className="card create-ticket-card">
            <div className="card-body">
              <h4 className="card-title section-title">Create New Ticket</h4>
              <form onSubmit={handleCreateTicketAndGuests}>
                <div className="mb-3">
                  <label htmlFor="ticketName" className="form-label">Ticket Name</label>
                  <input
                    type="text"
                    className="form-control"
                    id="ticketName"
                    value={newTicketName}
                    onChange={(e) => setNewTicketName(e.target.value)}
                    required
                  />
                </div>

                <h5>Guests for this Ticket:</h5>
                {newGuests.map((guest, index) => (
                  <div key={index} className="row mb-2 align-items-center">
                    <div className="col-4">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="First Name"
                        value={guest.first_name}
                        onChange={(e) => handleNewGuestChange(index, 'first_name', e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-4">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Last Name"
                        value={guest.last_name}
                        onChange={(e) => handleNewGuestChange(index, 'last_name', e.target.value)}
                      />
                    </div>
                    <div className="col-1">
                      <button type="button" className="btn btn-danger btn-sm" onClick={() => handleRemoveNewGuest(index)}>X</button>
                    </div>
                  </div>
                ))}
                <button type="button" className="btn btn-secondary mb-3 add-guest-btn" onClick={handleAddNewGuest}>Add Another Guest</button>
                <div>
                  <button type="submit" className="btn-like-tab">Create Ticket</button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div>
          {/* Display All Tickets and Guests */}
          <h3 className="mt-5 section-title">Current Tickets & Guests</h3>
          {tickets.length === 0 ? (
            <p>No tickets found.</p>
          ) : (
            <table className="tickets-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Welcome Party</th>
                  <th>Wedding</th>
                  <th>Pool Party</th>
                  <th>Venue</th>
                  <th>Dietary</th>
                  <th>Comments</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map(ticket => (
                  <React.Fragment key={ticket.id}>
                    <tr>
                      <td style={{ textAlign: 'left' }}>{ticket.name}</td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td style={{ fontSize: '14px', maxWidth: '200px' }}>{ticket.dietary_restrictions || '-'}</td>
                      <td style={{ fontSize: '14px', maxWidth: '200px' }}>{ticket.comments || '-'}</td>
                    </tr>
                    {ticket.guests.map(guest => (
                      <tr key={guest.id}>
                        <td style={{ textAlign: 'center' }}>{guest.first_name}</td>
                        <td>{guest.attending_welcome_party === true ? '☑️' : guest.attending_welcome_party === false ? '❌' : '☐'}</td>
                        <td>{guest.attending_wedding === true ? '☑️' : guest.attending_wedding === false ? '❌' : '☐'}</td>
                        <td>{guest.attending_pool_party === true ? '☑️' : guest.attending_pool_party === false ? '❌' : '☐'}</td>
                        <td>{guest.staying_at_venue === true ? '☑️' : guest.staying_at_venue === false ? '❌' : '☐'}</td>
                        <td></td>
                        <td></td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </section>
  );
};

export default AdminPage;